import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  fetchCapDocsIfNeeded,
  fetchCapEsignUrlIfNeeded,
  fetchProspectIfNeeded,
  fetchSalesRepIfNeeded,
  fetchUserIfNeeded,
  getACHEligible,
  getEligibleForAutopayByCreditCard,
  getCapDocs,
  getCapEsignUrl,
  getContactId,
  getContactIds,
  getContract,
  getContractId,
  getContractStatus,
  getCurrentOwner,
  getDeactivatedStatus,
  getFailedDeactivationStatus,
  getFriendlyName,
  getHasAutoPay,
  getMainUserLoaded,
  getMasterId,
  getOpportunity,
  getPrePTOStatus,
  getProjectId,
  getOpptyName,
  getProposalId,
  getProposalNumber,
  getProspect,
  getProspectId,
  getProspectLoaded,
  getRelatedContact,
  getSOWId,
  getSalesRepInfo,
  getServiceContractId,
  getUserAccounts,
  getUserAgreementProdcutType,
  getUserAgreementType,
  getUserError,
  getUserInfo,
  getUserLeadSource,
  getUserLoading,
  getUserMonthlyType,
  getUserPurchasedThru,
  getUserUpdating,
  getActiveChartType,
} from '../redux/modules/user';
import { COSTCO } from '../constants/PurchasedThruType';
import { IneligibleForReferral } from '../constants/LeadSources';
import { ORANGE_POST_PTO, ORANGE_PRE_PTO } from '../constants/customerTypes';
import { BLUE } from '../constants/billingSystems';

export const useUserInfo = () => {
  const user = useSelector(getUserInfo);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchUserIfNeeded());
  }, []);

  return user;
};

export const useMainUserLoaded = () => useSelector(getMainUserLoaded);

export const useUserLoading = () => useSelector(getUserLoading);

export const useProspectLoaded = () => useSelector(getProspectLoaded);

export const usePurchasedThru = () => useSelector(getUserPurchasedThru);

export const useActiveChartType = () => {
  const prospectId = useProspectId();
  const activeChart = useSelector(getActiveChartType);
  const dispatch = useDispatch();

  useEffect(() => {
    if (prospectId) {
      dispatch(fetchProspectIfNeeded(prospectId));
    }
  }, [prospectId]);

  return activeChart;
};

export const useLeadSource = () => useSelector(getUserLeadSource);

export const useReferralEligible = () => {
  const purchasedThru = usePurchasedThru();
  const leadSource = useLeadSource();

  if (purchasedThru?.toLowerCase() === COSTCO.toLowerCase() || IneligibleForReferral.includes(leadSource)) {
    return false;
  }

  return true;
};

export const useRelatedContact = () => useSelector(getRelatedContact);

export const useCurrentOwner = () => useSelector(getCurrentOwner);

export const useAgreementType = () => useSelector(getUserAgreementType);

export const useAgreementProductType = () => useSelector(getUserAgreementProdcutType);

export const useMonthlyType = () => useSelector(getUserMonthlyType);

export const useProspectId = () => useSelector(getProspectId);

export const useUserAccounts = () => useSelector(getUserAccounts);

export const useContract = () => useSelector(getContract);

export const useContactId = () => useSelector(getContactId);

export const useContactIds = () => useSelector(getContactIds);

export const useProjectId = () => useSelector(getProjectId);

export const useOpptyName = () => useSelector(getOpptyName);

export const useSOWId = () => useSelector(getSOWId);

export const useContractStatus = () => useSelector(getContractStatus);

export const usePrePTOStatus = () => useSelector(getPrePTOStatus);

export const useFailedDeactivationStatus = () => useSelector(getFailedDeactivationStatus);

export const useDeactivatedStatus = () => useSelector(getDeactivatedStatus);

export const useHasAutoPay = () => useSelector(getHasAutoPay);

export const useIsEligibleForAutopayByCreditCard = () => useSelector(getEligibleForAutopayByCreditCard);

export const useSalesRepInfo = () => {
  const salesRep = useSelector(getSalesRepInfo);
  const prospectId = useSelector(getProspectId);
  const dispatch = useDispatch();

  useEffect(() => {
    if (prospectId) {
      dispatch(fetchSalesRepIfNeeded(prospectId));
    }
  }, [prospectId]);

  return salesRep.get('data')[0];
};

export const useFriendlyName = () => {
  const friendlyName = useSelector(getFriendlyName);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchUserIfNeeded());
  }, []);

  return friendlyName;
};

export const useProposalId = () => {
  const proposalId = useSelector(getProposalId);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchUserIfNeeded());
  }, []);

  return proposalId;
};

export const useProposalNumber = () => {
  const proposalNumber = useSelector(getProposalNumber);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchUserIfNeeded());
  }, []);

  return proposalNumber;
};

export const useContractId = () => {
  const contractId = useSelector(getContractId);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchUserIfNeeded());
  }, []);

  return contractId;
};

export const useServiceContractId = () => {
  const serviceContractId = useSelector(getServiceContractId);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchUserIfNeeded());
  }, []);

  return serviceContractId;
};

export const useOpportunity = () => {
  const dispatch = useDispatch();
  const oppty = useSelector(getOpportunity);

  useEffect(() => {
    dispatch(fetchUserIfNeeded());
  });
  return oppty;
};

export const useProspect = () => {
  const dispatch = useDispatch();
  const prospectId = useProspectId();
  const prospect = useSelector(getProspect);

  useEffect(() => {
    if (prospectId) {
      dispatch(fetchProspectIfNeeded(prospectId));
    }
  }, [prospectId]);
  return prospect;
};

export const useBillingEnabled = () => {
  const oppty = useOpportunity();
  const purchasedThru = usePurchasedThru();

  const programTwoStates = ['CO', 'CT', 'FL', 'HI', 'NM', 'NY', 'SC', 'TX'];

  return purchasedThru !== COSTCO || (oppty && purchasedThru === COSTCO && programTwoStates.includes(oppty.state));
};

export const useCustomerType = () => {
  const oppty = useOpportunity();
  return oppty?.customerType;
};

export const useIsFundBridgeCustomer = () => {
  const oppty = useOpportunity();
  return oppty?.customerType === ORANGE_PRE_PTO;
};

export const useIsOrangePostPTO = () => {
  const oppty = useOpportunity();
  return oppty?.customerType === ORANGE_POST_PTO;
};

export const useHasIncompleteBatteryRetro = () => {
  const oppty = useOpportunity();

  return oppty?.childOpportunities?.some(child => child.productOffering === 'Retrofit Storage' && !child.contract?.siteAuditCompleted);
};

export const useIncompleteBatteryRetroOppty = () => {
  const oppty = useOpportunity();

  const children = oppty?.childOpportunities?.filter(child => child.productOffering === 'Retrofit Storage' && !child.contract?.siteAuditCompleted);
  if (!children || children.length === 0) {
    return null;
  }

  return children[0];
};

export const useIsEVChargerProspect = () => {
  const oppty = useOpportunity();

  return oppty?.productOffering?.toLowerCase() === 'ev charger install';
};

export const useIsPVHasEV = () => {
  const accounts = useUserAccounts();
  const isEVChargerProspect = useIsEVChargerProspect();

  if (isEVChargerProspect) {
    return false;
  }

  const hasEVChargerProspect = accounts?.find(oppty => oppty.productOffering?.toLowerCase() === 'ev charger install');

  return !!hasEVChargerProspect;
};

export const useIsSRSignOffDone = () => {
  // todo: return value when we have it from api
  return false;
};

export const useIsBlueBillingSystem = () => {
  const oppty = useOpportunity();

  return oppty?.contract?.billingSystem === BLUE || oppty?.customerType !== ORANGE_POST_PTO;
};

export const useACHEligible = () => {
  const isBlueBillingSystem = useIsBlueBillingSystem();
  const accountEligible = useSelector(getACHEligible);
  const oppty = useOpportunity();
  const isRelatedContact = useRelatedContact();
  const isEVChargerProspect = useIsEVChargerProspect();

  return accountEligible && oppty && !oppty.isMultiFamilyHome && isBlueBillingSystem && !isRelatedContact && !isEVChargerProspect;
};

export const useIsCustomerOwnedSystem = () => {
  const agreementType = useAgreementType();
  const customerOwnedPartialTypes = ['customer owned', 'customer-owned'];

  if (!agreementType) {
    return false;
  }

  return customerOwnedPartialTypes.includes(agreementType.toLowerCase());
};

export const useUserUpdating = () => useSelector(getUserUpdating);

export const useMasterId = () => useSelector(getMasterId);

export const useUserError = () => {
  const error = useSelector(getUserError);

  return error;
};

export const useIsMultiFamilyHome = () => {
  const oppty = useOpportunity();

  if (!oppty) {
    return null;
  }

  return oppty.isMultiFamilyHome;
};

export const useCapDocs = prospectId => {
  const dispatch = useDispatch();
  const capDocs = useSelector(getCapDocs);
  useEffect(() => {
    if (!prospectId) {
      return;
    }
    dispatch(fetchCapDocsIfNeeded(prospectId));
  }, [prospectId]);
  return capDocs;
};

export const useCapEsignUrl = (documentId, documentType, customerSigned, isProposalDocument, contactId) => {
  const dispatch = useDispatch();
  const capEsignUrl = useSelector(getCapEsignUrl);
  useEffect(() => {
    if (!documentId || !documentType || !customerSigned || !isProposalDocument || !contactId) {
      return;
    }
    dispatch(fetchCapEsignUrlIfNeeded(documentId, documentType, customerSigned, isProposalDocument, contactId));
  }, [documentId, documentType, customerSigned, isProposalDocument, contactId]);
  return capEsignUrl;
};
